/**
 * DocumentsPage
 *
 * Displays the customer's shared documents with download buttons.
 * Supports pagination for customers with many documents.
 * Read-only — customers can view and download but not upload.
 *
 * Requirements: Task 10 - Documents Page
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardContent,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@bloomcode/ui';
import { FileText, RefreshCw, ChevronLeft, ChevronRight } from '@bloomcode/icons';

import { useMyDocuments } from '@/hooks/useMyDocuments';
import { DocumentRow } from '@/components/documents';

/**
 * Empty state when no documents exist
 */
function EmptyState() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-16 text-center">
        <FileText className="h-12 w-12 text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No Documents Yet</h3>
        <p className="text-muted-foreground max-w-sm">
          When your account team shares documents or files with you, they will appear here.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Pagination controls
 */
function PaginationControls({
  page,
  totalPages,
  hasNext,
  hasPrev,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between mt-4">
      <p className="text-sm text-muted-foreground">
        Page {page} of {totalPages}
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrev}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNext}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function DocumentsPage() {
  const [page, setPage] = useState(1);

  const { documents, pagination, totalCount, isLoading, error, refetch } = useMyDocuments({
    page,
    limit: 10,
  });

  // Loading state
  if (isLoading && documents.length === 0) {
    return (
      <>
        <Helmet>
          <title>Documents | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-heading font-bold">Documents</h1>
            <p className="text-muted-foreground">Access documents and files shared with you.</p>
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="animate-pulse flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-muted" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-48 rounded bg-muted" />
                      <div className="h-3 w-32 rounded bg-muted" />
                    </div>
                    <div className="h-8 w-20 rounded bg-muted" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error && documents.length === 0) {
    return (
      <>
        <Helmet>
          <title>Documents | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-heading font-bold">Documents</h1>
          </div>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-destructive mb-2">Failed to load documents</p>
              <p className="text-sm text-muted-foreground mb-4">{error.message}</p>
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Documents | Bloomcode CRM Portal</title>
      </Helmet>

      <div className="container mx-auto p-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Documents</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Page Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold">Documents</h1>
            <p className="text-muted-foreground">
              {totalCount > 0
                ? `${totalCount} document${totalCount !== 1 ? 's' : ''} shared with you`
                : 'Access documents and files shared with you.'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
            aria-label="Refresh documents"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Document List or Empty State */}
        {documents.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="space-y-3" role="list" aria-label="Documents list">
              {documents.map((doc) => (
                <div key={doc.id} role="listitem">
                  <DocumentRow document={doc} />
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <PaginationControls
                page={pagination.page}
                totalPages={pagination.pages}
                hasNext={pagination.hasNext}
                hasPrev={pagination.hasPrev}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
